/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, CSSProperties, ReactElement } from 'react';
import { useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { CodeBlockSharedCssClassName } from '@atlaskit/editor-common/styles/code-block';
import type { CodeBlock as OriginalCodeBlock } from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	codeBlock: {
		backgroundColor: token('elevation.surface.raised'),
		boxSizing: 'border-box',
		minWidth: '48px',
		// Platform's lightweight-wrapper rule has higher specificity than a standalone
		// atomic class, so name the authored position explicitly on the wrapper.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors -- Match Platform's document-level first-code-block margin contract without counting DOM siblings.
		'.ak-renderer-document > .light-weight-code-block[data-first-top-level-node] &.code-block': {
			marginTop: token('space.0'),
		},
	},
	scroller: {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small'),
		boxSizing: 'border-box',
		direction: 'ltr',
		display: 'block',
		font: token('font.code'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches the Platform Renderer dense code-block geometry.
		fontSize: '0.875em',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Matches the Platform Renderer dense code-block geometry.
		lineHeight: '1.5em',
		margin: token('space.0'),
		// One code line plus block padding also keeps empty blocks at Platform's height.
		minHeight: `calc(1.5em + ${token('space.100')} + ${token('space.100')})`,
		overflowX: 'auto',
		overflowY: 'hidden',
		position: 'relative',
		width: '100%',
	},
	code: {
		boxSizing: 'border-box',
		color: token('color.text'),
		display: 'block',
		font: 'inherit',
		minWidth: 0,
		paddingBlock: token('space.100'),
		paddingInlineStart: 'var(--light-code-line-number-gutter, 0px)',
		tabSize: 4,
		whiteSpace: 'pre',
	},
	wrapped: {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	trailingNumberedLine: {
		// Platform's final line-number marker keeps a trailing empty line in layout.
		// Generated content reserves that line without changing the authored text.
		'&::after': {
			content: '"\\200B"',
		},
	},
});

type LightCodeBlockProps = ComponentProps<typeof OriginalCodeBlock> &
	LightNodeProps & {
		isFirstTopLevelNode?: boolean;
	};

const getCodeLineNumberGutterWidth = (text: string): string => {
	let lineCount = 1;
	let newlineIndex = text.indexOf('\n');

	while (newlineIndex !== -1) {
		lineCount += 1;
		newlineIndex = text.indexOf('\n', newlineIndex + 1);
	}

	return `calc(${String(lineCount).length}ch + 24px)`;
};

export const CodeBlock = (props: LightCodeBlockProps): ReactElement => {
	const { formatMessage } = useIntl();
	const shouldWrap = Boolean(props.allowWrapCodeBlock && props.wrap);
	const lineNumberGutterWidth = props.hideLineNumbers
		? '0px'
		: getCodeLineNumberGutterWidth(props.text);

	return (
		<div
			ref={props.lightNodeRef}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's lightweight code-block style contract.
			className={`light-weight-code-block ${props.className ?? ''}`}
			data-first-top-level-node={props.isFirstTopLevelNode ? '' : undefined}
			data-language={props.language}
			data-local-id={props.localId}
		>
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform's code-block style hook.
				className={CodeBlockSharedCssClassName.CODEBLOCK_CONTAINER}
				css={styles.codeBlock}
			>
				<pre
					// Light nodes must remain effect-free. Unwrapped blocks retain a tab stop even
					// when their content fits, avoiding observers and layout measurements.
					tabIndex={shouldWrap ? undefined : 0}
					// A scroll target is a named group, not a page landmark. Multiple code
					// blocks may share this label without creating duplicate region landmarks.
					role={shouldWrap ? undefined : 'group'}
					aria-label={
						!shouldWrap
							? formatMessage({
									id: 'content-renderer.code-block.scrollable-content',
									defaultMessage: 'Scrollable content',
									description: 'Accessible label for a horizontally scrollable code block',
								})
							: undefined
					}
					data-ds--code--code-block=""
					css={styles.scroller}
					style={
						{
							'--light-code-line-number-gutter': lineNumberGutterWidth,
						} as CSSProperties
					}
				>
					{/* eslint-disable-next-line @atlaskit/design-system/no-html-code -- A native code element is the inert semantic subtree; the ADS component would add unnecessary rendering behavior. */}
					<code
						css={[
							styles.code,
							shouldWrap && styles.wrapped,
							!props.hideLineNumbers && props.text.endsWith('\n') && styles.trailingNumberedLine,
						]}
					>
						{props.text}
					</code>
				</pre>
			</div>
		</div>
	);
};
